// Port of webapp/src/components/kanban/kanbanColumn.tsx
// Drag and drop uses native HTML5 DnD instead of upstream's react-dnd backend.

import {DragEvent, ReactElement} from 'react'
import classnames from 'classnames'
import {BoardGroup, IPropertyOption, IPropertyTemplate} from '../../types/board'
import {Card} from '../../types/card'
import {IUser} from '../../types/user'
import KanbanCard from './kanbanCard'
import KanbanColumnHeader from './kanbanColumnHeader'
import {Icon} from '../common/icons'

type Props = {
    group: BoardGroup
    groupByProperty: IPropertyTemplate | undefined
    activeProps: IPropertyTemplate[]
    usersById: Record<string, IUser>
    isCollapsed: boolean
    readOnly: boolean
    draggingCardId: string | null
    onCardClicked: (cardId: string) => void
    onToggleCollapsed: (optionId: string) => void
    onAddCard: (optionId: string) => void
    onDragStart: (card: Card) => void
    onDragEnd: () => void
    onDropOnColumn: (optionId: string, targetCard: Card | undefined, draggedId: string) => void
    onTitleChange: (option: IPropertyOption, value: string) => void
    onColorChange: (option: IPropertyOption, color: string) => void
    onHideGroup: (option: IPropertyOption) => void
    onAddOption: () => void
}

export default function KanbanColumn(props: Props): ReactElement {
    const {group, isCollapsed, draggingCardId} = props
    const optionId = group.option.id

    const allowDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
    }

    const drop = (event: DragEvent<HTMLDivElement>, targetCard: Card | undefined): void => {
        event.preventDefault()
        event.stopPropagation()
        // the id travels in dataTransfer so the drop still lands when the drag
        // state has not been committed yet (fast drags, synthetic events)
        props.onDropOnColumn(optionId, targetCard, event.dataTransfer.getData('text/plain'))
    }

    return (
        <div
            className='octo-board-column'
            data-group={optionId}
            onDragOver={allowDrop}
            onDrop={(event) => drop(event, undefined)}
        >
            <KanbanColumnHeader
                groupByProperty={props.groupByProperty}
                option={group.option}
                cardCount={group.cards.length}
                isCollapsed={isCollapsed}
                readOnly={props.readOnly}
                onTitleChange={props.onTitleChange}
                onColorChange={props.onColorChange}
                onToggleCollapsed={props.onToggleCollapsed}
                onHideGroup={props.onHideGroup}
                onAddCard={props.onAddCard}
                onAddOption={props.onAddOption}
            />

            {!isCollapsed && (
                <div
                    className='flex min-h-[40px] flex-col'
                    onDragOver={allowDrop}
                    onDrop={(event) => drop(event, undefined)}
                >
                    {group.cards.map((card) => (
                        <div
                            key={card.id}
                            onDragOver={allowDrop}
                            onDrop={(event) => drop(event, card)}
                        >
                            <KanbanCard
                                card={card}
                                activeProps={props.activeProps}
                                usersById={props.usersById}
                                onClick={props.onCardClicked}
                                onDragStart={() => props.onDragStart(card)}
                                onDragEnd={props.onDragEnd}
                                isDragging={draggingCardId === card.id}
                            />
                        </div>
                    ))}

                    {group.cards.length === 0 && !props.readOnly && (
                        <button
                            type='button'
                            className={classnames('BoardCard flex items-center gap-2 text-left opacity-60')}
                            onClick={() => props.onAddCard(optionId)}
                        >
                            <Icon name='add' size={14}/>
                            <span className='text-xs'>新建</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}
